"use client";

import { PooledPostingType } from "@nimble/data-service-client/types";
import {
  DEFAULT_LOCALE,
  formatJobPostingDate,
  JOB_POSTING_RECENTLY_THRESHOLD_DAYS,
} from "@nimble/ts-formatters/format-date";
import React, { createContext, ReactNode, useContext, useMemo } from "react";

import BulletedList from "@/components/shared/bulleted-list/bulleted-list";
import DistrictWideTag from "@/components/shared/jobs/district-wide-tag";
import {
  formatEmploymentType,
  toEmploymentType,
} from "@/lib/helpers/format-employment-type";

export interface JobBulletData {
  subcategories?: string[] | null;
  employment_type?: string | null;
  date_posted?: string | null;
  pooled_posting_type?: string | null;
}

interface FormattedJobBullets {
  job: JobBulletData;
  category: string | null;
  employmentType: string | null;
  datePosted: string | null;
}

const getFormattedJobBullets = (job: JobBulletData): FormattedJobBullets => {
  const { subcategories, employment_type, date_posted } = job;
  const hasSubcategories =
    Array.isArray(subcategories) && subcategories.length > 0;

  const formattedDate = date_posted
    ? formatJobPostingDate(
        date_posted,
        Date.now(),
        JOB_POSTING_RECENTLY_THRESHOLD_DAYS,
        DEFAULT_LOCALE,
      )
    : null;

  return {
    job,
    category: hasSubcategories ? subcategories.join(", ") : null,
    employmentType: employment_type
      ? formatEmploymentType(toEmploymentType(employment_type))
      : null,
    datePosted: formattedDate,
  };
};

const JobBulletsContext = createContext<FormattedJobBullets | null>(null);

function useJobBullets<T>(selector: (state: FormattedJobBullets) => T): T {
  const state = useContext(JobBulletsContext);
  if (!state) {
    throw new Error(
      "JobPostingBullets sub-components must be used within a JobPostingBullets provider",
    );
  }
  return selector(state);
}

interface JobPostingBulletsProps {
  job: JobBulletData;
  className?: string;
  children?: ReactNode;
}

export default function JobPostingBullets({
  job,
  className,
  children,
}: JobPostingBulletsProps) {
  const value = useMemo(() => getFormattedJobBullets(job), [job]);

  return (
    <JobBulletsContext.Provider value={value}>
      <BulletedList className={className}>
        {children || (
          <>
            <JobPostingBullets.Category />
            <JobPostingBullets.EmploymentType />
            <JobPostingBullets.DatePosted />
            <JobPostingBullets.Tags />
          </>
        )}
      </BulletedList>
    </JobBulletsContext.Provider>
  );
}

JobPostingBullets.Category = function Category() {
  const category = useJobBullets((state) => state.category);
  if (!category) return null;
  return (
    <span className="min-w-0 max-w-full whitespace-normal break-words">
      {category}
    </span>
  );
};

JobPostingBullets.EmploymentType = function EmploymentType() {
  const employmentType = useJobBullets((state) => state.employmentType);
  if (!employmentType) return null;
  return <span>{employmentType}</span>;
};

JobPostingBullets.DatePosted = function DatePosted() {
  const datePosted = useJobBullets((state) => state.datePosted);
  if (!datePosted) return null;
  return <span>Posted: {datePosted}</span>;
};

JobPostingBullets.Tags = function Tags({ children }: { children?: ReactNode }) {
  const isDistrictWide = useJobBullets(
    (state) =>
      state.job.pooled_posting_type === PooledPostingType.POOLED_POSTING,
  );
  const hasCustomChildren = React.Children.toArray(children).some(Boolean);

  if (!isDistrictWide && !hasCustomChildren) return null;

  return (
    <div className="inline-flex items-center [&>*:not(:first-child)]:ml-1.5">
      {isDistrictWide && <DistrictWideTag className="text-[11px]" />}
      {children}
    </div>
  );
};
